import React, { useCallback, useRef, useState } from 'react';
import { LOUDNESS_MAX_DB } from './tinnitogramLogic';

/**
 * LoudnessMatchGraph — three-bar chart:
 *   [0] Tinnitus Loudness (red)
 *   [1] Minimum Masking Level MML (blue)
 *   [2] Residual Inhibition RI (green — displays duration as label)
 * Y axis 0..60 dB SL. Click at column X, height Y writes the value.
 */
const LoudnessMatchGraph = ({
  loudness,           // { right: {loudness_db_sl, mml_db_sl, ri_present, ri_minutes}, left: {...} }
  activeEar = 'right',
  onSetEar,
  onSetValue,         // (ear, key, value) => void
  readOnly = false,
  width = 460, height = 240,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const marginL = 40, marginR = 12, marginT = 26, marginB = 42;
  const plotW = width - marginL - marginR;
  const plotH = height - marginT - marginB;
  const barW = plotW / 4;
  const cols = [
    { key: 'loudness_db_sl', label: 'Tinnitus Loudness', color: '#dc2626' },
    { key: 'mml_db_sl',      label: 'Minimum Masking Level (MML)', color: '#2563eb' },
    { key: 'ri_present',     label: 'Residual Inhibition (RI)', color: '#059669' },
  ];
  const colX = (idx) => marginL + barW * (idx + 0.5);
  const yScale = useCallback((db) => marginT + ((LOUDNESS_MAX_DB - db) * plotH) / LOUDNESS_MAX_DB, [plotH]);
  const yInv = useCallback((py) => {
    const raw = LOUDNESS_MAX_DB - ((py - marginT) * LOUDNESS_MAX_DB) / plotH;
    return Math.max(0, Math.min(LOUDNESS_MAX_DB, Math.round(raw)));
  }, [plotH]);

  const toViewBox = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };

  const snap = (p) => {
    if (!p) return null;
    if (p.y < marginT - 4 || p.y > height - marginB + 4) return null;
    if (p.x < marginL - 4 || p.x > width - marginR + 4) return null;
    let bestIdx = 0, bestDx = Infinity;
    cols.forEach((_, i) => {
      const dx = Math.abs(colX(i) - p.x);
      if (dx < bestDx) { bestDx = dx; bestIdx = i; }
    });
    return { idx: bestIdx, db: yInv(Math.max(marginT, Math.min(height - marginB, p.y))) };
  };

  const handleClick = (evt) => {
    if (readOnly) return;
    const s = snap(toViewBox(evt));
    if (!s) return;
    const col = cols[s.idx];
    if (col.key === 'ri_present') {
      // clicking RI column sets ri_present=true and stores minutes = db (interpret
      // dragged value as minutes on that column).
      onSetValue(activeEar, 'ri_present', true);
      onSetValue(activeEar, 'ri_minutes', s.db);
    } else {
      onSetValue(activeEar, col.key, s.db);
    }
  };

  const l = loudness?.[activeEar] || {};
  const yTicks = [0, 10, 20, 30, 40, 50, 60];

  return (
    <div className="w-full">
      {!readOnly && (
        <div className="flex items-center gap-1 mb-1" data-testid="loudness-controls">
          <span className="text-[10px] text-slate-500 font-semibold uppercase mr-1">Ear:</span>
          {['right', 'left'].map((e) => (
            <button key={e} type="button" data-testid={`loudness-ear-${e}`}
              onClick={() => onSetEar && onSetEar(e)}
              className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${activeEar === e ? 'text-white' : 'bg-slate-100 text-slate-600'}`}
              style={activeEar === e ? { backgroundColor: e === 'right' ? '#dc2626' : '#2563eb' } : {}}
            >{e === 'right' ? 'RE' : 'LE'}</button>
          ))}
          {!readOnly && l.ri_present && (
            <button type="button" data-testid="loudness-ri-clear" onClick={() => { onSetValue(activeEar, 'ri_present', false); onSetValue(activeEar, 'ri_minutes', null); }}
              className="ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-600">Clear RI</button>
          )}
        </div>
      )}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%" height={height}
        preserveAspectRatio="none"
        onClick={handleClick}
        onMouseMove={(e) => { const s = snap(toViewBox(e)); setHover(s); }}
        onMouseLeave={() => setHover(null)}
        data-testid="loudness-graph"
        style={{ cursor: readOnly ? 'default' : 'crosshair', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block' }}
      >
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={marginL} x2={width - marginR} y1={yScale(t)} y2={yScale(t)} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={marginL - 6} y={yScale(t) + 3} fontSize="9" fill="#64748b" textAnchor="end">{t}</text>
          </g>
        ))}
        <text x={marginL - 32} y={marginT + plotH / 2 - 20} fontSize="9" fill="#475569" transform={`rotate(-90, ${marginL - 32}, ${marginT + plotH / 2})`} textAnchor="middle">Intensity (dB SL)</text>
        {cols.map((c, i) => {
          const cx = colX(i);
          const isRI = c.key === 'ri_present';
          const val = isRI ? (l.ri_present ? (l.ri_minutes ?? 20) : null) : l[c.key];
          const shown = val !== null && val !== undefined && Number.isFinite(Number(val));
          const barTop = shown ? yScale(Number(val)) : yScale(0);
          const barBottom = yScale(0);
          return (
            <g key={c.key}>
              {shown && <rect x={cx - barW * 0.28} y={barTop} width={barW * 0.56} height={Math.max(0, barBottom - barTop)} fill={c.color} rx="2" />}
              {!shown && <rect x={cx - barW * 0.28} y={barBottom - 2} width={barW * 0.56} height="2" fill="#cbd5e1" />}
              {shown && (
                <>
                  <rect x={cx - 34} y={Math.max(marginT + 4, barTop - 22)} width="68" height="18" rx="3" fill="#ffffff" stroke={c.color} strokeWidth="1" />
                  <text x={cx} y={Math.max(marginT + 16, barTop - 8)} fontSize="10" fill={c.color} textAnchor="middle" fontWeight="700">
                    {isRI ? `Yes (${val} min)` : `${val} dB SL`}
                  </text>
                </>
              )}
              <text x={cx} y={height - marginB + 14} fontSize="9" fill="#334155" textAnchor="middle" fontWeight="600">
                {isRI ? 'Residual' : c.label.split(' ').slice(0, 2).join(' ')}
              </text>
              <text x={cx} y={height - marginB + 24} fontSize="8" fill="#64748b" textAnchor="middle">
                {isRI ? 'Inhibition (RI)' : c.label.split(' ').slice(2).join(' ')}
              </text>
            </g>
          );
        })}
        {/* Hover */}
        {!readOnly && hover && (
          <g style={{ pointerEvents: 'none' }} data-testid="loudness-hover">
            <line x1={colX(hover.idx)} x2={colX(hover.idx)} y1={marginT} y2={height - marginB} stroke={cols[hover.idx].color} strokeWidth="0.6" strokeDasharray="2 2" opacity="0.5" />
            <rect x={colX(hover.idx) + 8} y={Math.max(marginT, yScale(hover.db) - 10)} width="52" height="14" rx="2" fill="#0f172a" opacity="0.85" />
            <text x={colX(hover.idx) + 10} y={Math.max(marginT, yScale(hover.db) - 10) + 10} fontSize="9" fill="#ffffff" fontWeight="600">
              {cols[hover.idx].key === 'ri_present' ? `${hover.db} min` : `${hover.db} dB`}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default LoudnessMatchGraph;
